import {FC, ReactNode} from "react";
import {styled} from "@mui/material/styles";
import {t} from "../../../styles/theme/tokens";

/**
 * MenuGroup — обёртка для группы пунктов меню с опциональным заголовком.
 *
 * Заголовок:
 *   - 11px (caption минус один шаг), uppercase, letter-spacing 0.06em, weight 600
 *   - цвет text.secondary, mt:12, mb:4
 *   - служит визуальным разделителем между смысловыми блоками меню
 *
 * Без заголовка:
 *   - просто контейнер с верхним отступом, отделяющим от предыдущей группы
 */

const GroupRoot = styled("div", {
    shouldForwardProp: (prop) => prop !== "withTitle",
})<{ withTitle: boolean }>(({withTitle}) => ({
    display: "flex",
    flexDirection: "column",
    marginTop: withTitle ? 16 : 4,

    "&:first-of-type": {
        marginTop: 0,
    },
}));

/**
 * styled('div') вместо styled(Typography): здесь нам не нужны фичи MUI
 * Typography (variant/gutterBottom/...), а styled(Typography) в MUI v6+
 * теряет polymorphic-типизацию `component` prop. Чистый styled('div')
 * даёт ту же стилизацию и нормальную типизацию.
 */
const GroupTitle = styled("div")({
    fontSize: "0.6875rem",            // 11px
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: t.text.secondary,
    paddingInline: 16,
    paddingTop: 6,
    paddingBottom: 6,
    margin: 0,
    userSelect: "none",
});

interface MenuGroupProps {
    title?: string;
    children: ReactNode;
}

const MenuGroup: FC<MenuGroupProps> = ({title, children}) => {
    return (
        <GroupRoot withTitle={!!title}>
            {title && <GroupTitle>{title}</GroupTitle>}
            {children}
        </GroupRoot>
    );
};

export default MenuGroup;